import React, { useState, useEffect } from 'react';
import { Download, Trash2 } from 'lucide-react';
import { getApiUrl } from '../config/api';

const REPORT_AREAS = [
  { value: 'Research', label: 'Equity Intelligence™', destination: '/equity-intelligence' },
  { value: 'Sector', label: 'Sector Intelligence™', destination: '/sector-intelligence' },
  { value: 'Legal', label: 'Legal Intelligence™', destination: '/legal-intelligence' },
  { value: 'Learning', label: 'Professional Intelligence™', destination: '/professional-intelligence' },
  { value: 'UPSC', label: 'Civil Services Intelligence™', destination: '/civil-services-intelligence' },
];

const CATEGORY_FILTER_CONFIG = {
  Research: {
    primaryLabel: 'Sector',
    primaryOptions: ['All Sectors', 'Agri Inputs / Fertilisers', 'Auto & Auto Ancillaries', 'Banking', 'Capital Goods / Industrials', 'Cement & Building Materials', 'Chemicals / Specialty Chemicals', 'Consumer Durables', 'Defence / Aerospace', 'Financial Services / NBFC', 'FMCG', 'Hotels / Travel / Tourism', 'Information Technology', 'Infrastructure / Construction / EPC', 'Insurance & Capital Markets', 'Logistics / Transport Services', 'Media & Entertainment', 'Metals & Mining', 'Oil & Gas / Energy', 'Pharma & Healthcare', 'Power / Utilities', 'Railways / PSU Theme', 'Real Estate / REITs', 'Retail / Consumer Services', 'Telecom', 'Textiles / Apparel'],
  },
  Sector: {
    primaryLabel: 'Sector',
    primaryOptions: ['All sectors', 'Auto', 'Banking', 'Banking August 2026', 'Banking Intelligence', 'Capital Goods', 'Defence', 'Defence Sector', 'FMCG', 'Information Technology', 'Metals', 'NBFC Intelligence', 'Pharma', 'Power'],
  },
  Legal: {
    primaryLabel: 'Legal Area',
    primaryOptions: ['Constitutional', 'Construction Intel'],
  },
  Learning: {
    primaryLabel: 'Topic',
    primaryOptions: ['All', 'AI for Finance Professionals', 'Corporate Governance', 'Corporate Law Intelligence', 'Cost and Accounting Management Intelligence', 'Risk Management', 'Tax Intelligence'],
    secondaryLabel: 'Level',
    secondaryOptions: ['All Levels', 'AI Foundations', 'Advanced', 'Basic', 'Corporate Law', 'Costing', 'Direct Tax', 'Enterprise Risk', 'Executive AI', 'Finance Functions', 'Governance', 'Indirect Tax', 'Planning and Control', 'Strategic Cost Management', 'Transactions'],
  },
  UPSC: {
    primaryLabel: 'Content Type',
    primaryOptions: ['UPSC'],
  },
};

const getDefaultFilterValues = (category) => {
  const config = CATEGORY_FILTER_CONFIG[category] || CATEGORY_FILTER_CONFIG.Research;
  return {
    primaryFilter: config.primaryOptions[0],
    secondaryFilter: config.secondaryOptions?.[0] || '',
  };
};

const normalizeAdminRole = (role) => String(role || '').trim().toUpperCase().replace(/^ROLE_/, '');

const AdminReports = () => {
  const [user, setUser] = useState(null);
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uploadForm, setUploadForm] = useState({
    title: '',
    code: '',
    description: '',
    category: 'Research',
    primaryFilter: getDefaultFilterValues('Research').primaryFilter,
    secondaryFilter: getDefaultFilterValues('Research').secondaryFilter || '',
    htmlFile: null,
  });
  const [message, setMessage] = useState('');
  const [messageType, setMessageType] = useState('');
  const [deletingReportId, setDeletingReportId] = useState(null);
  const [codeSearch, setCodeSearch] = useState('');
  const [savingMetadataId, setSavingMetadataId] = useState(null);
  const [codeDrafts, setCodeDrafts] = useState({});
  const [descriptionDrafts, setDescriptionDrafts] = useState({});
  const [reportsLoading, setReportsLoading] = useState(true);
  const [exportingUsers, setExportingUsers] = useState(false);

  const selectedArea = REPORT_AREAS.find((area) => area.value === uploadForm.category) || REPORT_AREAS[0];
  const activeFilterConfig = CATEGORY_FILTER_CONFIG[uploadForm.category] || CATEGORY_FILTER_CONFIG.Research;
  const filteredReportsForDeletion = reports.filter((report) => {
    const query = codeSearch.trim().toLowerCase();
    if (!query) return true;
    const searchTarget = `${report.code || ''} ${report.title || ''}`.toLowerCase();
    return searchTarget.includes(query);
  });

  useEffect(() => {
    checkAuth();
    fetchReports();
  }, []);

  const checkAuth = async () => {
    try {
      const API = getApiUrl();
      const accessToken = localStorage.getItem('access_token');
      const resp = await fetch(`${API}/auth/me`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}),
        },
        credentials: 'include',
      });
      if (resp.ok) {
        const userData = await resp.json();
        setUser(userData);
        if (normalizeAdminRole(userData.role) !== 'ADMIN_USER') {
          setMessage('Access denied: Admin role required');
          setMessageType('error');
        }
      } else {
        setUser(null);
        setMessage('Please log in to access admin panel');
        setMessageType('error');
      }
    } catch (err) {
      console.error('Auth check failed:', err);
      setUser(null);
    }
  };

  const fetchReports = async () => {
    try {
      const API = getApiUrl();
      const accessToken = localStorage.getItem('access_token');
      const headers = {};
      if (accessToken) {
        headers.Authorization = `Bearer ${accessToken}`;
      }

      setReportsLoading(true);
      const resp = await fetch(`${API}/subscription-reports`, {
        credentials: 'include',
        headers,
      });
      if (resp.ok) {
        const data = await resp.json();
        const loadedReports = Array.isArray(data) ? data : [];
        setReports(loadedReports);
        setCodeDrafts(Object.fromEntries(
          loadedReports.map((report) => [report.id, report.code || ''])
        ));
        setDescriptionDrafts(Object.fromEntries(
          loadedReports.map((report) => [report.id, report.description || ''])
        ));
        setMessage((currentMessage) => (
          currentMessage.startsWith('Failed to load existing reports') ? '' : currentMessage
        ));
      } else {
        setReports([]);
        setMessage(`Failed to load existing reports (Status: ${resp.status})`);
        setMessageType('error');
      }
    } catch (err) {
      console.error('Failed to fetch reports:', err);
      setReports([]);
      setMessage('Failed to load existing reports: ' + err.message);
      setMessageType('error');
    } finally {
      setReportsLoading(false);
    }
  };

  const handleCategoryChange = (e) => {
    const nextCategory = e.target.value;
    const defaults = getDefaultFilterValues(nextCategory);
    setUploadForm((current) => ({
      ...current,
      category: nextCategory,
      primaryFilter: defaults.primaryFilter,
      secondaryFilter: defaults.secondaryFilter,
    }));
  };

  const handleUpload = async (e) => {
    e.preventDefault();
    setLoading(true);
    setMessage('');

    if (!uploadForm.title.trim()) {
      setMessage('Please enter a report title.');
      setMessageType('error');
      setLoading(false);
      return;
    }

    if (!uploadForm.htmlFile) {
      setMessage('Please select an HTML report file to upload.');
      setMessageType('error');
      setLoading(false);
      return;
    }

    if (!uploadForm.primaryFilter || (activeFilterConfig.secondaryOptions?.length > 0 && !uploadForm.secondaryFilter)) {
      setMessage(activeFilterConfig.secondaryOptions?.length > 0
        ? `Please select both ${activeFilterConfig.primaryLabel} and ${activeFilterConfig.secondaryLabel}.`
        : `Please select ${activeFilterConfig.primaryLabel}.`);
      setMessageType('error');
      setLoading(false);
      return;
    }

    try {
      const API = getApiUrl();
      const formData = new FormData();
      formData.append('title', uploadForm.title);
      formData.append('code', uploadForm.code);
      formData.append('description', uploadForm.description);
      formData.append('category', uploadForm.category);
      formData.append('primaryFilter', uploadForm.primaryFilter);
      formData.append('secondaryFilter', uploadForm.secondaryFilter);
      formData.append('htmlFile', uploadForm.htmlFile);

      const url = `${API}/admin/reports/upload`;
      const accessToken = localStorage.getItem('access_token');
      const headers = {};
      if (accessToken) {
        headers['Authorization'] = `Bearer ${accessToken}`;
      }

      const resp = await fetch(url, {
        method: 'POST',
        credentials: 'include',
        headers: headers,
        body: formData,
      });

      if (resp.ok) {
        setMessage('Report uploaded successfully!');
        setMessageType('success');
        const nextCategory = uploadForm.category || 'Research';
        const defaults = getDefaultFilterValues(nextCategory);
        setUploadForm({
          title: '',
          code: '',
          description: '',
          category: nextCategory,
          primaryFilter: defaults.primaryFilter,
          secondaryFilter: defaults.secondaryFilter || '',
          htmlFile: null,
        });
        fetchReports();
      } else {
        const detail = await readErrorMessage(resp);
        setMessage(detail || `Upload failed (Status: ${resp.status})`);
        setMessageType('error');
      }
    } catch (err) {
      console.error('Upload failed:', err);
      setMessage('Upload failed: ' + err.message);
      setMessageType('error');
    } finally {
      setLoading(false);
    }
  };

  const readErrorMessage = async (resp) => {
    try {
      const contentType = resp.headers.get('content-type') || '';
      if (contentType.includes('application/json')) {
        const data = await resp.json();
        return data.message || data.error || data.detail || '';
      }
      const text = await resp.text();
      return text || '';
    } catch {
      return '';
    }
  };

  const handleCodeDraftChange = (reportId, value) => {
    setCodeDrafts((current) => ({
      ...current,
      [reportId]: value,
    }));
  };

  const handleDescriptionDraftChange = (reportId, value) => {
    setDescriptionDrafts((current) => ({
      ...current,
      [reportId]: value,
    }));
  };

  const handleSaveMetadata = async (report) => {
    if (!report?.id) {
      return;
    }

    const code = codeDrafts[report.id] || '';
    const description = descriptionDrafts[report.id] || '';
    if (code.length > 100) {
      setMessage('Code must be 100 characters or fewer.');
      setMessageType('error');
      return;
    }

    if (description.length > 600) {
      setMessage('Description must be 600 characters or fewer.');
      setMessageType('error');
      return;
    }

    setSavingMetadataId(report.id);
    setMessage('');

    try {
      const API = getApiUrl();
      const accessToken = localStorage.getItem('access_token');
      const headers = { 'Content-Type': 'application/json' };
      if (accessToken) {
        headers.Authorization = `Bearer ${accessToken}`;
      }

      const resp = await fetch(`${API}/admin/reports/${report.id}/metadata`, {
        method: 'PATCH',
        credentials: 'include',
        headers,
        body: JSON.stringify({ code, description }),
      });

      if (resp.ok) {
        const cleanedCode = code.trim();
        const cleanedDescription = description.trim();
        setReports((currentReports) => currentReports.map((item) => (
          item.id === report.id ? { ...item, code: cleanedCode, description: cleanedDescription } : item
        )));
        setCodeDrafts((current) => ({
          ...current,
          [report.id]: cleanedCode,
        }));
        setDescriptionDrafts((current) => ({
          ...current,
          [report.id]: cleanedDescription,
        }));
        setMessage(`Saved metadata for "${report.title}".`);
        setMessageType('success');
      } else {
        const detail = await readErrorMessage(resp);
        setMessage(detail || `Metadata save failed (Status: ${resp.status})`);
        setMessageType('error');
      }
    } catch (err) {
      console.error('Metadata save failed:', err);
      setMessage('Metadata save failed: ' + err.message);
      setMessageType('error');
    } finally {
      setSavingMetadataId(null);
    }
  };

  const handleExportUsers = async () => {
    setExportingUsers(true);
    setMessage('');

    try {
      const API = getApiUrl();
      const accessToken = localStorage.getItem('access_token');
      const response = await fetch(`${API}/admin/users/export`, {
        credentials: 'include',
        headers: accessToken ? { Authorization: `Bearer ${accessToken}` } : {},
      });

      if (!response.ok) {
        setMessage(`User export failed (Status: ${response.status})`);
        setMessageType('error');
        return;
      }

      const downloadUrl = URL.createObjectURL(await response.blob());
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = 'users.csv';
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(downloadUrl);
      setMessage('User list exported.');
      setMessageType('success');
    } catch (err) {
      console.error('User export failed:', err);
      setMessage('User export failed: ' + err.message);
      setMessageType('error');
    } finally {
      setExportingUsers(false);
    }
  };

  const handleDeleteReport = async (report) => {
    if (!report?.id) {
      return;
    }

    const title = report.title || `Report ${report.id}`;
    const confirmed = window.confirm(`Delete "${title}"? This will permanently remove the report from the database.`);
    if (!confirmed) {
      return;
    }

    setDeletingReportId(report.id);
    setMessage('');

    try {
      const API = getApiUrl();
      const accessToken = localStorage.getItem('access_token');
      const headers = {};
      if (accessToken) {
        headers.Authorization = `Bearer ${accessToken}`;
      }

      const resp = await fetch(`${API}/admin/reports/${report.id}`, {
        method: 'DELETE',
        credentials: 'include',
        headers,
      });

      if (resp.ok) {
        setReports((currentReports) => currentReports.filter((item) => item.id !== report.id));
        setMessage(`Deleted "${title}".`);
        setMessageType('success');
      } else {
        setMessage(`Delete failed (Status: ${resp.status})`);
        setMessageType('error');
      }
    } catch (err) {
      console.error('Delete failed:', err);
      setMessage('Delete failed: ' + err.message);
      setMessageType('error');
    } finally {
      setDeletingReportId(null);
    }
  };

  if (!user || normalizeAdminRole(user.role) !== 'ADMIN_USER') {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="bg-white p-8 rounded-lg shadow-md">
          <h1 className="text-2xl font-bold text-red-600 mb-4">Access Denied</h1>
          <p className="text-gray-600">Admin role required to access this page.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="mb-8 flex flex-wrap items-start justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Admin Reports Management</h1>
            <p className="text-gray-600 mt-2">Upload and manage subscription reports</p>
          </div>
          <button
            type="button"
            onClick={handleExportUsers}
            disabled={exportingUsers}
            title="Export registered users as CSV"
            className="inline-flex min-h-10 items-center gap-2 rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-60"
          >
            <Download className="h-4 w-4" aria-hidden="true" />
            {exportingUsers ? 'Exporting…' : 'Export users CSV'}
          </button>
        </div>

        {message && (
          <div className={`mb-6 p-4 rounded-lg ${messageType === 'success' ? 'bg-green-50 text-green-800' : 'bg-red-50 text-red-800'}`}>
            {message}
          </div>
        )}

        <div className="grid grid-cols-1 gap-8">
          {/* Upload Form */}
          <div className="bg-white rounded-lg shadow-md p-6 max-w-3xl">
            <h2 className="text-xl font-semibold mb-4">Upload New Report</h2>
            <form onSubmit={handleUpload} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Title</label>
                <input
                  type="text"
                  required
                  value={uploadForm.title}
                  onChange={(e) => setUploadForm({ ...uploadForm, title: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Code</label>
                <input
                  type="text"
                  value={uploadForm.code}
                  onChange={(e) => setUploadForm({ ...uploadForm, code: e.target.value })}
                  maxLength={100}
                  placeholder="Example: HDFC, APNT, RPT-001"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
                <p className="mt-1 text-xs text-gray-500">
                  Stored in the reports.code column. {uploadForm.code.length}/100
                </p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Short Description</label>
                <textarea
                  value={uploadForm.description}
                  onChange={(e) => setUploadForm({ ...uploadForm, description: e.target.value })}
                  maxLength={600}
                  rows={3}
                  placeholder="Briefly explain what this report covers."
                  className="w-full resize-y px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
                <p className="mt-1 text-xs text-gray-500">
                  This appears under the report type on report cards. {uploadForm.description.length}/600
                </p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Category</label>
                <select
                  value={uploadForm.category}
                  onChange={handleCategoryChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  {REPORT_AREAS.map((area) => (
                    <option key={area.value} value={area.value}>{area.label}</option>
                  ))}
                </select>
                <p className="mt-1 text-sm text-gray-600">
                  This report will appear in the {selectedArea.label} area ({selectedArea.destination}).
                </p>
              </div>
              <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">{activeFilterConfig.primaryLabel}</label>
                  <select
                    value={uploadForm.primaryFilter}
                    onChange={(e) => setUploadForm((current) => ({ ...current, primaryFilter: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    required
                  >
                    {activeFilterConfig.primaryOptions.map((option) => (
                      <option key={option} value={option}>{option}</option>
                    ))}
                  </select>
                </div>
                {activeFilterConfig.secondaryOptions?.length > 0 && <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">{activeFilterConfig.secondaryLabel}</label>
                  <select
                    value={uploadForm.secondaryFilter}
                    onChange={(e) => setUploadForm((current) => ({ ...current, secondaryFilter: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    required
                  >
                    {activeFilterConfig.secondaryOptions.map((option) => (
                      <option key={option} value={option}>{option}</option>
                    ))}
                  </select>
                </div>}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">HTML File</label>
                <input
                  type="file"
                  required
                  accept=".html,.htm"
                  onChange={(e) => setUploadForm({ ...uploadForm, htmlFile: e.target.files[0] })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
                {uploadForm.htmlFile && (
                  <p className="mt-1 text-sm text-gray-600">Selected: {uploadForm.htmlFile.name}</p>
                )}
              </div>
              <button
                type="submit"
                disabled={loading}
                className="w-full bg-blue-600 text-white py-2 px-4 rounded-md hover:bg-blue-700 disabled:bg-gray-400"
              >
                {loading ? 'Uploading...' : 'Upload Report'}
              </button>
            </form>
          </div>
        </div>

        {/* Reports List */}
        <div className="mt-8 bg-white rounded-lg shadow-md p-6">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between mb-4">
            <h2 className="text-xl font-semibold">Existing Reports</h2>
            <div className="w-full max-w-xs">
              <label className="block text-xs font-medium text-gray-600 mb-1">Search by code or title</label>
              <input
                type="text"
                value={codeSearch}
                onChange={(e) => setCodeSearch(e.target.value)}
                placeholder="e.g. HDFC or report title"
                className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>
          {reportsLoading ? (
            <p className="text-gray-500">Loading reports...</p>
          ) : filteredReportsForDeletion.length === 0 ? (
            <p className="text-gray-500">No reports match the current search.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Title</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Code</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Description</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Category</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Filters</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Pages</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Created By</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Created At</th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {filteredReportsForDeletion.map((report) => (
                    <tr key={report.id}>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{report.title}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        <input
                          type="text"
                          value={codeDrafts[report.id] ?? ''}
                          onChange={(e) => handleCodeDraftChange(report.id, e.target.value)}
                          maxLength={100}
                          placeholder="Add code"
                          className="w-40 rounded-md border border-gray-300 px-2 py-1 text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-500 max-w-xs">
                        <textarea
                          value={descriptionDrafts[report.id] ?? ''}
                          onChange={(e) => handleDescriptionDraftChange(report.id, e.target.value)}
                          maxLength={600}
                          rows={2}
                          placeholder="Add short description"
                          className="w-72 resize-y rounded-md border border-gray-300 px-2 py-1 text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                        <div className="mt-2 flex items-center justify-between gap-3">
                          <span className="text-xs text-gray-400">
                            {(descriptionDrafts[report.id] || '').length}/600
                          </span>
                          <button
                            type="button"
                            onClick={() => handleSaveMetadata(report)}
                            disabled={
                              savingMetadataId === report.id ||
                              (
                                (codeDrafts[report.id] || '').trim() === (report.code || '').trim() &&
                                (descriptionDrafts[report.id] || '').trim() === (report.description || '').trim()
                              )
                            }
                            className="rounded-md border border-blue-200 px-3 py-1 text-xs font-semibold text-blue-700 hover:bg-blue-50 disabled:cursor-not-allowed disabled:opacity-50"
                          >
                            {savingMetadataId === report.id ? 'Saving...' : 'Save'}
                          </button>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{report.category}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {report.primaryFilter || '-'} / {report.secondaryFilter || '-'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{report.totalPages ?? report.total_pages}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{report.createdBy}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {new Date(report.createdAt || report.created_at).toLocaleDateString()}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <button
                          type="button"
                          onClick={() => handleDeleteReport(report)}
                          disabled={deletingReportId === report.id}
                          className="inline-flex items-center gap-2 rounded-md border border-red-200 px-3 py-2 text-sm font-semibold text-red-700 hover:bg-red-50 disabled:cursor-not-allowed disabled:opacity-60"
                          title={`Delete ${report.title}`}
                        >
                          <Trash2 className="h-4 w-4" />
                          {deletingReportId === report.id ? 'Deleting...' : 'Delete'}
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminReports;